import createClient, { type Middleware } from "openapi-fetch";
import type { paths } from "./gen/schema";
import { toApiError } from "./problem";

/**
 * The base URL is an env var, injected by Vite at build time (see deploy/docker/web.Dockerfile).
 * Nothing in the app hard-codes a host: flipping from the MSW mocks to the real Go API is a
 * change of environment, never a change of code.
 */
export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8080";

const TOKEN_KEY = "tankly.token";
const EXPIRES_KEY = "tankly.token.expires_at";

/**
 * The session token. The spec says the browser's real credential is an HTTP-only cookie —
 * which JS cannot read, and that is the point. We send `credentials: "include"` so the
 * cookie always goes; the bearer token is kept only for environments where a cross-origin
 * cookie will not be set (the MSW mocks, and the `localhost:5173 → localhost:8080` dev split).
 * Belt and braces, and the server accepts either — which is exactly why a refresh MUST
 * update this: an unrefreshed bearer would silently override a perfectly good cookie with a
 * dead token and turn "the session refreshed" into "every request 401s anyway".
 *
 * `expiresAt` rides along so `SessionProvider` can schedule a refresh BEFORE the access
 * token dies, without needing its own copy of a fact this store already holds.
 */
export const tokenStore = {
  get(): string | null {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set(token: string | null, expiresAt?: string | null): void {
    try {
      if (token) localStorage.setItem(TOKEN_KEY, token);
      else localStorage.removeItem(TOKEN_KEY);

      // `expiresAt` is optional so a caller that only means "sign out" (`set(null)`) does not
      // have to also think about the expiry key — clearing the token clears both.
      if (!token || expiresAt === null) localStorage.removeItem(EXPIRES_KEY);
      else if (expiresAt) localStorage.setItem(EXPIRES_KEY, expiresAt);
    } catch {
      // private mode — the session simply does not survive a reload
    }
  },
  getExpiresAt(): string | null {
    try {
      return localStorage.getItem(EXPIRES_KEY);
    } catch {
      return null;
    }
  },
};

const authMiddleware: Middleware = {
  onRequest({ request }) {
    const token = tokenStore.get();
    if (token) request.headers.set("Authorization", `Bearer ${token}`);
    return request;
  },
};

/* ------------------------------------------------------------- session refresh
 *
 * The backend issues a 1-hour access token and a 30-day refresh token (cookie-backed for the
 * browser). Left alone, that means every tab silently dies an hour after login — this is the
 * fix. Two mechanisms share ONE implementation (`refreshSession`) so they can never disagree
 * about what "refreshed" or "expired" means:
 *
 *   1. A 401 on any non-auth request triggers a refresh-and-retry, below.
 *   2. `SessionProvider` also calls `refreshSession()` proactively, a few minutes before the
 *      access token's `expires_at` — so the 401 path is a safety net, not the normal case.
 */

export interface RefreshedSession {
  accessToken: string;
  expiresAt: string;
}

type AuthEvent = { type: "refreshed"; expiresAt: string } | { type: "expired" };

const authEventListeners = new Set<(event: AuthEvent) => void>();

/** `SessionProvider` subscribes to this — it is the only thing that knows about routing and
 * the query cache, both of which this module must not import (it would be a layering cycle:
 * hooks.ts → client.ts → session state → hooks.ts). */
export function onAuthEvent(listener: (event: AuthEvent) => void): () => void {
  authEventListeners.add(listener);
  return () => authEventListeners.delete(listener);
}

function emitAuthEvent(event: AuthEvent): void {
  for (const listener of authEventListeners) listener(event);
}

let refreshInFlight: Promise<RefreshedSession | null> | null = null;

/**
 * Rotate the refresh token and mint a fresh access token.
 *
 * SINGLE-FLIGHT: whichever caller arrives first starts the network call and every other
 * caller — however many concurrent 401s or a racing proactive timer — is handed the SAME
 * promise. The refresh token is single-use (the spec says reuse revokes the whole session
 * family), so firing it twice at once would sign the user out, not just waste a request.
 */
export function refreshSession(): Promise<RefreshedSession | null> {
  refreshInFlight ??= performRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function performRefresh(): Promise<RefreshedSession | null> {
  try {
    // No body: the browser's refresh token lives in an HttpOnly cookie scoped to this path,
    // and `credentials: "include"` (set below, for every request this client makes) sends it
    // automatically. Nothing here ever reads or stores the refresh token in JS.
    const session = await unwrap(api.POST("/v1/auth/refresh", {}));
    tokenStore.set(session.access_token, session.expires_at);
    emitAuthEvent({ type: "refreshed", expiresAt: session.expires_at });
    return { accessToken: session.access_token, expiresAt: session.expires_at };
  } catch {
    // The refresh token is gone too (expired, or already consumed by someone else) — there
    // is no session left to salvage. Stop presenting a now-meaningless bearer token.
    tokenStore.set(null);
    emitAuthEvent({ type: "expired" });
    return null;
  }
}

/** `/v1/auth/*` is bootstrapping identity itself — a 401 there is a real credential failure
 * (wrong password, dead refresh token), never "the access token expired". Chasing THOSE with
 * another refresh is exactly how you build an infinite loop. */
function isAuthBootstrapRequest(url: string): boolean {
  try {
    return new URL(url).pathname.startsWith("/v1/auth/");
  } catch {
    return false;
  }
}

/** Requests currently in flight, keyed by openapi-fetch's per-request id, holding an
 * UNCONSUMED clone taken before `fetch` reads the body — the only point at which a body can
 * still be cloned. Populated in `onRequest`, drained in `onResponse` (every response, not
 * just 401s, or this leaks one entry per request forever). */
const pendingRequests = new Map<string, Request>();

const sessionRefreshMiddleware: Middleware = {
  onRequest({ request, id }) {
    pendingRequests.set(id, request.clone());
    return undefined;
  },
  async onResponse({ request, response, id }) {
    const original = pendingRequests.get(id);
    pendingRequests.delete(id);

    if (response.status !== 401) return undefined;
    if (isAuthBootstrapRequest(request.url)) return undefined;
    if (!original) return undefined;

    const refreshed = await refreshSession();
    if (!refreshed) return undefined; // refresh failed — the original 401 stands.

    // Retry exactly once, with the NEW token. Reusing `original` (not `request`, which the
    // fetch above has already consumed) is what makes retrying a POST/PATCH body possible at
    // all — and this whole retry is a raw `fetch`, never routed back through this client, so
    // there is no way for it to loop back into this middleware.
    const headers = new Headers(original.headers);
    headers.set("Authorization", `Bearer ${refreshed.accessToken}`);
    return globalThis.fetch(new Request(original, { headers }));
  },
};

export const api = createClient<paths>({
  baseUrl: API_BASE_URL,
  credentials: "include",
  /**
   * Resolve `fetch` at CALL time, not at module-eval time.
   *
   * openapi-fetch otherwise captures `globalThis.fetch` the moment this module is imported.
   * Anything that patches fetch afterwards — MSW (both the browser worker and the node
   * server), and later the OpenTelemetry fetch instrumentation this app is going to want —
   * is then bypassed entirely: the client holds the original function and calls straight
   * out to the network.
   *
   * That is not a test-only concern. It cost us an afternoon here: the mocks were live, the
   * handlers were correct, and every request sailed past them to the real backend.
   */
  fetch: (request) => globalThis.fetch(request),
});

api.use(authMiddleware);
api.use(sessionRefreshMiddleware);

interface FetchResult<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

/**
 * Collapse openapi-fetch's `{data, error}` into "value, or throw an ApiError".
 * React Query wants a promise that rejects; this is the one place that translation happens.
 */
export async function unwrap<T>(promise: Promise<FetchResult<T>>): Promise<T> {
  let result: FetchResult<T>;
  try {
    result = await promise;
  } catch (cause) {
    // The fetch itself failed — server down, DNS, CORS. There is no problem+json body.
    throw toApiError(cause);
  }

  const { data, error, response } = result;
  if (error !== undefined || !response.ok) throw toApiError(error, response);
  return data as T;
}

/**
 * A creation that must not be duplicated if the operator taps twice or the phone drops the
 * response. The spec supports `Idempotency-Key` on exactly the fuel-shaped endpoints.
 */
export function idempotencyKey(): string {
  return crypto.randomUUID();
}
